import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useFileMutations } from '.';
import { DocumentMetadataState } from '../components/fileDetailPanel/DocumentMetadataSection';
import { FileObjectRow, FileObjectRowsActions } from '../components/fileDetailPanel/types';
import type { DocumentFile } from '../types';
import { getDocumentMutationErrorMessage } from '../utils/documentErrors';

interface UseDocumentSubmitProps {
  companyId: string | null;
  currentFolderId: string | undefined;
  currentSearch?: string;
  metadataState: DocumentMetadataState;
  deviceAssignment: {
    deviceModelId: number;
    userLevel: number;
  } | undefined;
  shouldShowUserLevel: boolean;
  rows: FileObjectRow[];
  hasCreatedSession: boolean;
  validateForm: () => boolean;
  fileObjectsActions: FileObjectRowsActions;
  onCreated?: (file: DocumentFile) => void;
  onGeneralError: (error: string) => void;
}

interface SubmitStatus {
  isPending: boolean;
}

/**
 * Hook that orchestrates the full document-creation submission flow:
 * 1. Guards against duplicate submissions when a session is already created.
 * 2. Clears any previous general error, then validates the entire form.
 * 3. Filters out soft-deleted file rows before sending.
 * 4. Calls `createFileMutation` to create the document record, then calls
 *    `uploadFileObjectMutation` once per row so each file object is uploaded independently.
 * 5. Shows toast notifications for partial upload failures.
 * 6. Surfaces unexpected errors via `onGeneralError`.
 *
 * Returns `handleSubmit` and a `status` object with an `isPending` flag that
 * combines local pending state with the mutations' own pending states.
 */
export function useCreateFileSubmit({
  companyId,
  currentFolderId,
  currentSearch,
  metadataState,
  deviceAssignment,
  shouldShowUserLevel,
  rows,
  hasCreatedSession,
  validateForm,
  fileObjectsActions,
  onCreated,
  onGeneralError,
}: UseDocumentSubmitProps) {
  const { t } = useTranslation();

  const { createFileMutation, updateScopedFile, upsertFileObject } = useFileMutations({
    companyId,
    currentFolderId,
    search: currentSearch,
  });

  // Local pending flag kept separately so it remains true until the entire async flow
  // (including all row uploads) has settled, not just until the mutation resolves
  const [status, setStatus] = useState<SubmitStatus>({ isPending: false });

  /**
   * Initiates the document creation and file upload process.
   * - Prevents re-submission if the document record already exists (`hasCreatedSession`).
   * - Validates all form sections; aborts early if any section is invalid.
   * - Excludes rows marked as deleted from the payload.
   * - Creates the file record first, then uploads each file object row independently.
   * - Updates each file row's upload status in real time via `fileObjectsActions.setUploadStatus`.
   * - Displays toast errors for rows that failed during upload.
   */
  const handleSubmit = useCallback(async () => {
    if (hasCreatedSession) return;

    onGeneralError('');
    const isValid = validateForm();
    if (!isValid) return;

    const activeRows = rows.filter(
      (row: FileObjectRow) => row.changeState !== 'deleted'
    );

    const firstFile = activeRows[0]
    if (firstFile === undefined) {
      onGeneralError(t('documents.createFile.errors.noFilesToUpload'));
      toast.error(t('documents.createFile.errors.noFilesToUpload'))
      console.error('No files to upload');
      return
    }
    const fileType = activeRows[0]?.file?.type

    try {
      setStatus({ isPending: true });

      const createdFile = await createFileMutation.mutateAsync({
        folderId: metadataState.selectedFolder ?? null,
        name: metadataState.name.trim(),
        fileType: fileType,
        category: Number(metadataState.category),
        companyLevelId: metadataState.selectedCompanyLevel,
        deviceModelId: deviceAssignment?.deviceModelId,
        userLevel: deviceAssignment?.userLevel,
      });

      onCreated?.(createdFile);

      let uploadFailures = 0;

      const results = await fileObjectsActions.save(createdFile)
      upsertFileObject(createdFile.id, { isAvailable: true });
      results.map((row) => {
        if (row.state === 'created' && row.fileObject !== undefined) {
          upsertFileObject(createdFile.id, { isAvailable: true, id: row.objectId }, row.fileObject);
        }
      })

      // Set final upload state on the file card now that all rows have settled.
      // Done here rather than in onSuccess/onError so we only check after all uploads
      // are complete and all file objects are already in the cache.
      updateScopedFile(createdFile.id, (file) => ({
        ...file,
        uploadState: activeRows.length !== results.length ? { status: 'failed', progress: 0 } : undefined,
      }));

      if (uploadFailures > 0) {
        toast.error(t('documents.createFile.upload.failedRetry'));
      }

    } catch (error) {
      const message = getDocumentMutationErrorMessage(error, t);
      onGeneralError(message);
    } finally {
      setStatus({ isPending: false });
    }
  }, [
    hasCreatedSession,
    validateForm,
    rows,
    createFileMutation,
    updateScopedFile,
    metadataState,
    deviceAssignment,
    shouldShowUserLevel,
    fileObjectsActions,
    onCreated,
    onGeneralError,
    t,
  ]);

  // Merge local state with both mutations' pending flags so the caller always
  // sees a single consistent isPending signal
  const submitStatus: SubmitStatus = {
    isPending:
      status.isPending ||
      createFileMutation.isPending
  };

  return { handleSubmit, status: submitStatus };
}
